import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import userWinCheckService from '../../api/services/userWinCheck.service';
import type {
  WinCheckGameDetail,
  WinCheckGameListItem,
  WinCheckGrid,
  WinCheckPatterns,
  WinCheckUser,
} from '../../api/services/userWinCheck.service';
import {
  MagnifyingGlassIcon,
  TrophyIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

/* =========================================================
   PHONE NORMALIZER
   Accepts user input like 0912345678 and converts to
   +251912345678 for the backend query.
   Non-phone inputs (usernames, telegram ids) pass through
   unchanged so search still works for all three types.
========================================================= */
const ETHIOPIA_COUNTRY_CODE = '+251';

/**
 * Detect if a query is a phone-number-shaped input.
 * Rules:
 *  - digits only (optionally a leading +), optional spaces/dashes
 *  - OR starts with 0 followed by 9 digits (Ethiopian local)
 *  - OR starts with +251 followed by 9 digits
 */
function looksLikePhone(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;

  // Only allow digits, +, spaces, dashes, parens
  if (!/^[+\d\s\-()]+$/.test(trimmed)) return false;

  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits.length < 9) return false;

  // Ethiopian local: 0XXXXXXXXX
  if (/^0\d{9}$/.test(digits)) return true;

  // Ethiopian intl: 251XXXXXXXXX
  if (/^251\d{9}$/.test(digits)) return true;

  // Already has +251 prefix
  if (/^\+251\d{9}$/.test(trimmed.replace(/[\s\-()]/g, ''))) return true;

  // Generic phone-shaped (all digits, 9-15 length) — still try to normalize
  if (/^\d{9,15}$/.test(digits)) return true;

  return false;
}

/**
 * Normalize a phone-number-shaped input to E.164 (+251...).
 * - 0912345678    -> +251912345678
 * - 912345678     -> +251912345678
 * - 251912345678  -> +251912345678
 * - +251912345678 -> +251912345678
 * - anything else -> returned unchanged (username / telegramId)
 */
function normalizePhoneQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  if (!looksLikePhone(trimmed)) return trimmed;

  const digits = trimmed.replace(/[^\d]/g, '');

  // Local form: 0XXXXXXXXX
  if (/^0\d{9}$/.test(digits)) {
    return `${ETHIOPIA_COUNTRY_CODE}${digits.slice(1)}`;
  }

  // Already country code: 251XXXXXXXXX
  if (/^251\d{9}$/.test(digits)) {
    return `+${digits}`;
  }

  // Bare local 9-digit starting with 9 (Ethiopian mobile)
  if (/^9\d{8}$/.test(digits)) {
    return `${ETHIOPIA_COUNTRY_CODE}${digits}`;
  }

  // Fallback: if we got here with a phone shape, prefix +251 if
  // no country code was detected.
  if (/^\d{9,15}$/.test(digits)) {
    return `${ETHIOPIA_COUNTRY_CODE}${digits.replace(/^0+/, '')}`;
  }

  return trimmed;
}

/* =========================================================
   PATTERN ENGINE (unchanged)
========================================================= */
type Letter = 'B' | 'I' | 'N' | 'G' | 'O';
const LETTERS: Letter[] = ['B', 'I', 'N', 'G', 'O'];

interface Cell {
  letter: Letter;
  row: number;
}

interface PatternDef {
  indices: number[];
  kind: string;
  displayName: string;
}

const rowIndices = (r: number): number[] =>
  [0, 1, 2, 3, 4].map((c) => r * 5 + c);
const colIndices = (c: number): number[] =>
  [0, 1, 2, 3, 4].map((r) => r * 5 + c);

const DIAG_MAIN = [0, 6, 12, 18, 24];
const DIAG_ANTI = [4, 8, 12, 16, 20];
const CORNERS = [0, 4, 20, 24];
const SINGLE_MIDDLE = [6, 8, 16, 18];
const MIDDLE_CROSS = [7, 11, 13, 17];
const FULL = Array.from({ length: 25 }, (_, i) => i);

function cartelaToFlat(grid: WinCheckGrid): (number | null)[] {
  const { B, I, N, G, O } = grid;
  return [
    B[0], I[0], N[0], G[0], O[0],
    B[1], I[1], N[1], G[1], O[1],
    B[2], I[2], N[2], G[2], O[2],
    B[3], I[3], N[3], G[3], O[3],
    B[4], I[4], N[4], G[4], O[4],
  ];
}

function buildEnabledPatterns(p: WinCheckPatterns): PatternDef[] {
  const defs: PatternDef[] = [];

  if (p.horizontal) {
    for (let r = 0; r < 5; r++)
      defs.push({
        indices: rowIndices(r),
        kind: 'horizontal',
        displayName: `HORIZONTAL ROW ${r + 1}`,
      });
  }
  if (p.vertical) {
    for (let c = 0; c < 5; c++)
      defs.push({
        indices: colIndices(c),
        kind: 'vertical',
        displayName: `VERTICAL COLUMN ${LETTERS[c]}`,
      });
  }
  if (p.diagonal) {
    defs.push({
      indices: DIAG_MAIN,
      kind: 'diagonal',
      displayName: 'DIAGONAL ↘',
    });
    defs.push({
      indices: DIAG_ANTI,
      kind: 'diagonal',
      displayName: 'DIAGONAL ↙',
    });
  }
  if (p.singleCorner)
    defs.push({
      indices: CORNERS,
      kind: 'singleCorner',
      displayName: 'FOUR CORNERS',
    });
  if (p.singleMiddle)
    defs.push({
      indices: SINGLE_MIDDLE,
      kind: 'singleMiddle',
      displayName: 'SINGLE MIDDLE',
    });
  if (p.middleCross)
    defs.push({
      indices: MIDDLE_CROSS,
      kind: 'middleCross',
      displayName: 'MIDDLE CROSS',
    });
  if (p.full)
    defs.push({ indices: FULL, kind: 'full', displayName: 'FULL CLOSE' });

  return defs;
}

interface WinResult {
  cells: Cell[];
  patternName: string;
}

function findWinningPatterns(
  grid: WinCheckGrid,
  calledNumbers: number[],
  patterns: WinCheckPatterns
): WinResult[] {
  const flat = cartelaToFlat(grid);
  const called = new Set(calledNumbers);
  const lastCalled =
    calledNumbers.length > 0
      ? calledNumbers[calledNumbers.length - 1]
      : null;
  if (lastCalled === null) return [];

  const isMarked = (idx: number) => {
    const v = flat[idx];
    return v === null || called.has(v);
  };

  const enabled = buildEnabledPatterns(patterns);
  const complete = enabled.filter((p) => p.indices.every(isMarked));
  if (complete.length === 0) return [];

  const lastInSome = complete.some((p) =>
    p.indices.some((idx) => flat[idx] === lastCalled)
  );
  if (!lastInSome) return [];

  return complete.map((p) => ({
    cells: p.indices.map((idx) => ({
      letter: LETTERS[idx % 5],
      row: Math.floor(idx / 5),
    })),
    patternName: p.displayName,
  }));
}

/* =========================================================
   HELPERS
========================================================= */
const formatDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec}s`;
  return `${sec}s`;
};

const fmtAmount = (n: number | undefined | null): string => {
  if (n == null || !Number.isFinite(n)) return '0';
  const rounded = Math.round((n + Number.EPSILON) * 1000) / 1000;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

/* =========================================================
   MODAL
========================================================= */
interface UserWinCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserWinCheckModal: React.FC<UserWinCheckModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchInput, setSearchInput] = useState('');
  // The string actually sent to the backend — normalized
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [selectedCartela, setSelectedCartela] = useState<number | null>(null);
  const [extraCalls, setExtraCalls] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) {
      setSearchInput('');
      setSubmittedQuery('');
      setSelectedGameId(null);
      setSelectedCartela(null);
      setExtraCalls(0);
    }
  }, [isOpen]);

  const submitSearch = () => {
    const raw = searchInput.trim();
    if (!raw) {
      setSubmittedQuery('');
      return;
    }
    // 👇 normalize only at submit time — the visible input keeps
    //    exactly what the user typed (0912345678).
    setSubmittedQuery(normalizePhoneQuery(raw));
    setSelectedGameId(null);
    setSelectedCartela(null);
    setExtraCalls(0);
  };

  const {
    data: searchData,
    isFetching: isSearching,
    isFetched: searchFetched,
  } = useQuery({
    queryKey: ['user-win-check-find', submittedQuery],
    queryFn: () => userWinCheckService.findUser(submittedQuery),
    enabled: !!submittedQuery && isOpen,
    staleTime: 60 * 1000,
  });

  const user: WinCheckUser | null = searchData?.user ?? null;
  const games: WinCheckGameListItem[] = searchData?.games ?? [];

  const { data: detail, isFetching: isLoadingDetail } =
    useQuery<WinCheckGameDetail>({
      queryKey: [
        'user-win-check-game',
        user?.telegramId,
        selectedGameId,
      ],
      queryFn: () =>
        userWinCheckService.getUserGame(
          user!.telegramId,
          selectedGameId!
        ),
      enabled: !!user && !!selectedGameId && isOpen,
      staleTime: 60 * 1000,
    });

  useEffect(() => {
    setSelectedCartela(null);
    setExtraCalls(0);
  }, [selectedGameId]);

  useEffect(() => {
    if (
      detail &&
      selectedCartela == null &&
      detail.me.cartelaNumbers.length > 0
    ) {
      setSelectedCartela(detail.me.cartelaNumbers[0]);
    }
  }, [detail, selectedCartela]);

  const effectiveCalledNumbers = useMemo<number[]>(() => {
    if (!detail) return [];
    const base = detail.game.calledNumbers || [];
    if (extraCalls <= 0) return base;

    const already = new Set(base);
    const added: number[] = [];
    for (const n of detail.game.sequence) {
      if (already.has(n)) continue;
      added.push(n);
      if (added.length >= extraCalls) break;
    }
    return [...base, ...added];
  }, [detail, extraCalls]);

  const hasMoreSim = useMemo(() => {
    if (!detail) return false;
    const base = detail.game.calledNumbers.length;
    return base + extraCalls < detail.game.sequence.length;
  }, [detail, extraCalls]);

  const calledSet = useMemo(
    () => new Set(effectiveCalledNumbers),
    [effectiveCalledNumbers]
  );

  const selectedGrid: WinCheckGrid | null = useMemo(() => {
    if (!detail || selectedCartela == null) return null;
    const found = detail.cartelas.find(
      (c) => c.cartelaNumber === selectedCartela
    );
    return found?.grid ?? null;
  }, [detail, selectedCartela]);

  const analysis = useMemo(() => {
    if (!detail || !selectedGrid) {
      return {
        wins: [] as WinResult[],
        winCells: new Set<string>(),
        isWinNow: false,
        patternLabel: '',
      };
    }

    const wins = findWinningPatterns(
      selectedGrid,
      effectiveCalledNumbers,
      detail.game.patterns
    );

    const required = Math.max(
      1,
      detail.game.patternsRequiredToWin || 1
    );

    const winCells = new Set<string>();
    for (const w of wins) {
      for (const c of w.cells) winCells.add(`${c.letter}-${c.row}`);
    }

    const isWinNow = wins.length >= required;
    const patternLabel = wins
      .slice(0, required)
      .map((w) => w.patternName)
      .join(' + ');

    return { wins, winCells, isWinNow, patternLabel };
  }, [detail, selectedGrid, effectiveCalledNumbers]);

  const archivedWinner = useMemo(() => {
    if (!detail || selectedCartela == null) return null;
    return (
      detail.myWinners.find(
        (w) => w.cartelaNumber === selectedCartela
      ) ?? null
    );
  }, [detail, selectedCartela]);

  /* ── Renderers ── */
  const renderSearch = () => (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Enter a username, telegram id, or phone number to find the user.
        Phone numbers can be entered in local format (
        <span className="font-mono">0912345678</span>) — they will be
        matched as <span className="font-mono">+251912345678</span>.
      </p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch();
            }}
            placeholder="username / telegram id / phone (0912345678)"
            className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <button
          onClick={submitSearch}
          className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          Search
        </button>
      </div>

      {/* Show the normalized query only if it differs — helps
          admins confirm the number was converted. */}
      {submittedQuery &&
        submittedQuery !== searchInput.trim() &&
        looksLikePhone(searchInput) && (
          <p className="text-[10px] text-gray-500">
            Searching as{' '}
            <span className="font-mono text-gray-700">
              {submittedQuery}
            </span>
          </p>
        )}

      {isSearching && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {!isSearching && searchFetched && submittedQuery && (
        <>
          {!user ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No user found.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.displayName}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">
                    @{user.telegramId}
                    {user.username ? ` · ${user.username}` : ''}
                    {user.phoneNumber ? ` · ${user.phoneNumber}` : ''}
                  </p>
                </div>
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap ml-2">
                  ETB {fmtAmount(user.balance)}
                </span>
              </div>

              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">
                  Last {games.length} finished game
                  {games.length === 1 ? '' : 's'}
                </p>
                {games.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    This user has no finished games.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {games.map((g) => (
                      <button
                        key={g.id}
                        onClick={() => setSelectedGameId(g.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                          selectedGameId === g.id
                            ? 'border-primary-400 bg-primary-50'
                            : 'border-gray-100'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {g.historyId}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {g.roomId} ·{' '}
                            {new Date(g.finishedAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right whitespace-nowrap ml-2">
                          <p className="text-[10px] text-gray-500">
                            #{g.myCartelaNumbers.length} cartelas
                          </p>
                          <p className="text-[10px] font-medium text-gray-700">
                            Call #{g.callIndex}
                          </p>
                          {g.iAmWinner && (
                            <p className="text-[10px] text-green-600 font-semibold">
                              Winner
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderGamePanel = () => {
    if (!selectedGameId) return null;
    if (isLoadingDetail) {
      return (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      );
    }
    if (!detail) return null;

    const myCartelas = detail.me.cartelaNumbers;

    return (
      <div className="space-y-3">
        <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-900">
              {detail.game.historyId}
            </p>
            <p className="text-[10px] text-gray-500">
              {detail.game.roomId} · Bet ETB{' '}
              {fmtAmount(detail.game.betAmount)} ·{' '}
              {formatDuration(detail.game.durationMs)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500">
              Called {effectiveCalledNumbers.length} /{' '}
              {detail.game.sequence.length}
            </p>
            <p className="text-[10px] text-gray-500">
              Winner @ call #{detail.game.winnerCallIndex ?? '—'}
            </p>
          </div>
        </div>

        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">
            User's Cartelas ({myCartelas.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {myCartelas.map((num) => {
              const archivedWon =
                detail.me.wonCartelas.includes(num);
              return (
                <button
                  key={num}
                  onClick={() => setSelectedCartela(num)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                    selectedCartela === num
                      ? 'bg-primary-500 text-white border-primary-500'
                      : archivedWon
                      ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  #{num}
                  {archivedWon && ' 🏆'}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
          <div>
            <p className="text-[11px] font-medium text-yellow-800">
              Simulate next calls (front-end only)
            </p>
            <p className="text-[10px] text-yellow-700">
              +{extraCalls} future call{extraCalls === 1 ? '' : 's'}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setExtraCalls((n) => Math.max(0, n - 1))}
              disabled={extraCalls <= 0}
              className="p-1 rounded-md border border-yellow-300 bg-white disabled:opacity-40"
              title="Remove a simulated call"
            >
              <TrashIcon className="w-3.5 h-3.5 text-yellow-700" />
            </button>
            <button
              onClick={() => setExtraCalls((n) => n + 1)}
              disabled={!hasMoreSim}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-yellow-300 bg-white disabled:opacity-40"
              title="Simulate next call"
            >
              <PlusIcon className="w-3.5 h-3.5 text-yellow-700" />
              Next call
            </button>
            <button
              onClick={() => setExtraCalls(0)}
              disabled={extraCalls <= 0}
              className="px-2 py-1 text-xs rounded-md border border-yellow-300 bg-white disabled:opacity-40"
            >
              Reset
            </button>
          </div>
        </div>

        {selectedCartela != null && (
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Cartela #{selectedCartela}
              </p>
              {analysis.isWinNow ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                  <TrophyIcon className="w-3 h-3" />
                  WIN NOW · {analysis.patternLabel}
                </span>
              ) : archivedWinner ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                  <TrophyIcon className="w-3 h-3" />
                  Archived win ·{' '}
                  {archivedWinner.patternName || archivedWinner.pattern} · ETB{' '}
                  {fmtAmount(archivedWinner.prizeAmount)}
                </span>
              ) : (
                <span className="text-[10px] text-gray-500">
                  No winning pattern with current called set
                </span>
              )}
            </div>

            {archivedWinner && (
              <div className="flex items-center gap-2 mb-3 text-[11px]">
                <span className="text-gray-500">Balance:</span>
                <span className="font-medium text-gray-700">
                  ETB {fmtAmount(archivedWinner.balanceBefore)}
                </span>
                <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                <span className="font-semibold text-green-600">
                  ETB {fmtAmount(archivedWinner.balanceAfter)}
                </span>
              </div>
            )}

            {selectedGrid ? (
              <div className="inline-block">
                <div className="grid grid-cols-5 gap-1">
                  {LETTERS.map((letter) => (
                    <div
                      key={letter}
                      className="w-8 h-8 flex items-center justify-center text-xs font-bold text-primary-600 bg-primary-50 rounded"
                    >
                      {letter}
                    </div>
                  ))}
                  {[0, 1, 2, 3, 4].map((row) =>
                    LETTERS.map((letter) => {
                      const val =
                        selectedGrid[letter]?.[row] ?? null;
                      const isWinning = analysis.winCells.has(
                        `${letter}-${row}`
                      );
                      const isCalled =
                        val !== null && calledSet.has(val);

                      let cls =
                        'bg-white border border-gray-200 text-gray-700';
                      if (isWinning) {
                        cls =
                          'bg-green-500 text-white font-bold shadow-sm';
                      } else if (isCalled) {
                        cls =
                          'bg-yellow-400 text-yellow-900 font-semibold';
                      } else if (val === null) {
                        cls = 'bg-gray-200 text-gray-400';
                      }

                      return (
                        <div
                          key={`${letter}-${row}`}
                          className={`w-8 h-8 flex items-center justify-center text-xs font-medium rounded ${cls}`}
                        >
                          {val === null ? '★' : val}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Grid not available for this cartela.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-500" />
                Winning pattern
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-yellow-400" />
                Called
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-white border border-gray-200" />
                Not called
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-gray-200" />
                Free
              </span>
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
            Called Numbers ({effectiveCalledNumbers.length})
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1">
            {effectiveCalledNumbers.length === 0 ? (
              <p className="text-xs text-gray-500 py-2">
                No numbers called
              </p>
            ) : (
              effectiveCalledNumbers.map((num, idx) => {
                const isSimulated =
                  idx >= detail.game.calledNumbers.length;
                return (
                  <div
                    key={`${num}-${idx}`}
                    className={`w-8 h-8 flex items-center justify-center text-xs font-semibold rounded-full border ${
                      isSimulated
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        : 'bg-primary-50 text-primary-700 border-primary-100'
                    }`}
                    title={
                      isSimulated
                        ? `Simulated call #${idx + 1}`
                        : `Call #${idx + 1}`
                    }
                  >
                    {num}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {detail.game.remainingNumbers.length > 0 && (
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
              Remaining ({detail.game.remainingNumbers.length})
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1">
              {detail.game.remainingNumbers.map((num, idx) => (
                <div
                  key={`${num}-${idx}`}
                  className="w-8 h-8 flex items-center justify-center text-xs font-medium rounded-full bg-gray-100 text-gray-500"
                >
                  {num}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Check User Win"
      size="lg"
    >
      <div className="space-y-4">
        {renderSearch()}
        {renderGamePanel()}
      </div>
    </Modal>
  );
};

export default UserWinCheckModal;